export interface DealsColumn {
  id:
    | 'dealName'
    | 'stage'
    | 'client'
    | 'value'
    | 'dealsType'
    | 'currentStep'
    | 'closeDate'
    | 'owner'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export type DealType =
  | 'Commercial/Endorsement'
  | 'Event_Appearance'
  | 'Event_Owned'
  | 'Content/Production'
  | 'Licensing/IP'
  | 'Equity/Investment'
  | 'Branded Entertainment';

export const PIPELINE_MAP: Record<string, string> = {
  COMMERCIAL_ENDORSEMENT: 'Commercial/Endorsement',
  EVENT_APPEARANCE: 'Event_Appearance',
  EVENT_OWNED: 'Event_Owned',
  CONTENT_PRODUCTION: 'Content/Production',
  LICENSING_IP: 'Licensing/IP',
  EQUITY_INVESTMENT: 'Equity/Investment',
  BRANDED_ENT: 'Branded Entertainment',
};

export const DealsTableColumns: readonly DealsColumn[] = [
  { id: 'dealName', label: 'Deal Name' },
  { id: 'stage', label: 'Stage' },
  { id: 'client', label: 'Client' },
  { id: 'value', label: 'Value' },
  { id: 'dealsType', label: 'Deals Type' },
  { id: 'currentStep', label: 'Current Step' },
  { id: 'closeDate', label: 'Close Date' },
  { id: 'owner', label: 'Owner' },
  { id: 'actions', label: '', align: 'right' },
];

interface BaseDealDetails {
  name: string;
  clientName: string;
  accountId?: string;
  ownerName: string;
  revenueTier: string;
  ceilingTier: string;
  clientOrientation: string;
  careerState: string;

  dealValue?: string;
  commissionRate: number;
  commissionValue: number;
  dealStructure: string;
  equity: number;
  backend: string;
  closeDate: Date;

  leadershipSponsor: string;
  source: string;
  partnerAccount: string;
  partnerContact: string;
  daysInCurrentStage: string;

  outcome: string;
  lossReason: string;
  passReason: string;
  competitor: string;

  notes: string;
}

export type EventOwnedStage = string;
export type OwnershipStructure = string;
export type CommercialEndorsementStage = string;
export type ClientApprovalStatus = string;
export type ExclusivityScope = string;
export type CommercialDealType = string;
export type RenewalProbability = string;

export type EventAppearanceStage = string;
export type EventType = string;
export type ContentRights = string;
export type PaymentTerms = string;

export type LicensingIPStage = string;
export type LicenseType = string;
export type ReportingFrequency = string;

export type EquityInvestmentStage = string;
export type InvestmentType = string;
export type IndustrySector = string;
export type BoardSeatOption = string;

export type BrandedEntertainmentStage = string;
export type BuyerType = string;
export type EngagementType = string;

export interface ICommercialEndorsement extends BaseDealDetails {
  stageName: CommercialEndorsementStage;
  clientFitScore: number;
  clientApproval: ClientApprovalStatus;
  contractTerm: number;
  exclusivityScope: ExclusivityScope;
  category: string;
  dealType: CommercialDealType;
  renewalProbability: RenewalProbability;
}

export interface IEventAppearance extends BaseDealDetails {
  stageName: EventAppearanceStage;
  eventName: string;
  eventType: EventType;
  eventDate: Date;
  eventLocation: string;
  duration: number;
  contentRights: ContentRights;
  paymentTerms: PaymentTerms;
  travelRequired: boolean;
}

export interface IEventOwned extends BaseDealDetails {
  stageName: EventOwnedStage;
  eventPropertyName: string;
  ownershipStructure: OwnershipStructure;
  '3ASOwnership': number;
  expectedAttendance: number;
  ticketRevenueTarget: number;
  sponsorshipRevenueTarget: number;
  productionBudget: number;
  projectedMargin: number;
}

export interface ILicensingIP extends BaseDealDetails {
  stageName: LicensingIPStage;
  licenseType: LicenseType;
  AIRightsFlag: boolean;
  guaranteedMinimum: number;
  royaltyRate: number;
  territory: string;
  productApproval: string;
  reportingFrequency: ReportingFrequency;
}

export interface IEquityInvestment extends BaseDealDetails {
  stageName: EquityInvestmentStage;
  investmentType: InvestmentType;
  entityName: string;
  industrySector: IndustrySector;
  investmentAmount: number;
  ownershipTarget: number;
  preMoneyValuation: number;
  boardSeat: BoardSeatOption;
  targetExitMultiple: number;
}

export interface IBrandedEntertainment extends BaseDealDetails {
  stageName: BrandedEntertainmentStage;
  buyerType: BuyerType;
  organizationName: string;
  qualificationScore: number;
  clientPlacement: number;
  placedClients: string;
  engagementType: EngagementType;
  developmentFee: number;
  retainerValue: number;
  IPOwnership: number;
  lionsgateInvolved: boolean;
  recruitingPipeline: boolean;
}

type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'textarea'
  | 'section'
  | 'checkbox'
  | 'autocomplete';

export type FieldConfig = {
  name: string;
  label: string;
  type?: FieldType;
  options?: readonly string[];
  multiline?: boolean;
  minRows?: number;
  xs?: number;
  sm?: number;
  autocompleteType?: 'client' | 'owner';
  required?: boolean;
};
